import { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, Link, useNavigate } from 'react-router-dom';

import { Box, List, Typography, TypographyProps } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants, ROIMetricDisplayType, ROUTES } from '@arrived/common';
import { useTrackPortfolioAnalytics } from '@arrived/hooks';
import { AccountBalanceV2, AccountId, AccountOfferingBalance } from '@arrived/models';
import { useGetAccountBalanceQuery, useGetAccountOfferingBalancesQuery } from '@arrived/queries';

import { DataStateHandler } from '../../DataStateHandler';
import { PortfolioBalance } from './PortfolioBalance';
import { PortfolioOfferingListItem } from './PortfolioOfferingListItem';

import { NavigationBarContext } from '../../navigation';
import { PortfolioEquityTooltip } from '../common';
import { ROIMetricDisplayToggle } from '../offeringMetrics';
import {
  AppreciationAccordionDetails,
  AppreciationAccordionSummary,
  PortfolioAccordion,
  RentalIncomeAccordionDetails,
  RentalIncomeAccordionSummary,
} from './accordion';

const StyledPortfolio = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};

  --section-header-spacing: ${({ theme }) => theme.spacing(2)};

  .offering-list-header {
    position: sticky;
    background-color: ${({ theme }) => theme.palette.neutrals.white};
    display: flex;
    flex-direction: row;
    align-items: center;
    z-index: ${({ theme }) => theme.zIndex.mobileStepper};
    padding-bottom: var(--section-header-spacing);
    padding-top: var(--section-header-spacing);

    & > *:last-child {
      margin-left: auto;
    }
  }

  .offering-list {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .offering-list-item {
    padding: ${({ theme }) => theme.spacing(2.5, 2)};
    margin: ${({ theme }) => theme.spacing(0, -2)};
    width: auto;
    cursor: pointer;
  }

  .body-section {
    padding: ${({ theme }) => theme.spacing(0, 2)};
    display: flex;
    flex-direction: column;

    &:not(.offering-list-section) {
      gap: ${({ theme }) => theme.spacing(1)};
    }

    & > *:first-of-type:not(.offering-list-header) {
      margin-bottom: var(--section-header-spacing);
    }
  }

  .offering-list-section {
    margin-top: calc(-1 * var(--section-header-spacing));
  }

  .property-details-link {
    position: absolute;
    top: -9999px;
    left: -9999px;
  }
`;

const PortfolioSectionHeaderTypography = ({ children, ...rest }: Omit<TypographyProps, 'variant'>) => (
  <Typography {...rest} variant="h5">
    {children}
  </Typography>
);

export interface PortfolioMobileProps {
  accountId?: AccountId;
  className?: string;
  topOffset?: number;
}

export const PortfolioMobile = ({ accountId, ...rest }: PortfolioMobileProps) => {
  const accountBalanceState = useGetAccountBalanceQuery(accountId);
  const accountOfferingBalancesState = useGetAccountOfferingBalancesQuery(accountId);

  useTrackPortfolioAnalytics(accountBalanceState.data, accountOfferingBalancesState.data?.offerings);

  return (
    <DataStateHandler queries={[accountBalanceState, accountOfferingBalancesState]}>
      {(accountBalanceState, accountOfferingBalancesState) => (
        <PortfolioMobileContent
          accountBalance={accountBalanceState.data}
          accountOfferingBalances={accountOfferingBalancesState.data.offerings}
          {...rest}
        />
      )}
    </DataStateHandler>
  );
};

interface PortfolioMobileContentProps {
  accountBalance: AccountBalanceV2;
  accountOfferingBalances: AccountOfferingBalance[];
  className?: string;
  topOffset?: number;
}

const PortfolioMobileContent = ({
  accountBalance,
  accountOfferingBalances,
  className,
  topOffset,
}: PortfolioMobileContentProps) => {
  const [displayType, setDisplayType] = useState<ROIMetricDisplayType>(ROIMetricDisplayType.EQUITY);
  const navigate = useNavigate();
  const { offset, transition } = useContext(NavigationBarContext);

  const {
    appreciation: { totalAppreciation },
    initialInvestment,
    rentalIncome: { blendedAnnualDividendPercent, latestDividend, totalDividendsPaid },
    totalValue,
  } = accountBalance;

  return (
    <StyledPortfolio className={className}>
      <PortfolioBalance
        allTimeAppreciation={totalAppreciation}
        initialInvestment={initialInvestment}
        totalBalance={totalValue}
      />
      <section className="body-section">
        <PortfolioSectionHeaderTypography data-e2e="investment-performance">
          <FormattedMessage id="portfolio.mobile.investmentPerformance" />
        </PortfolioSectionHeaderTypography>
        <PortfolioAccordion>
          <AppreciationAccordionSummary allTimeAppreciation={totalAppreciation} />
          <AppreciationAccordionDetails
            allTimeAppreciation={totalAppreciation}
            initialInvestment={initialInvestment}
            totalBalance={totalValue}
          />
        </PortfolioAccordion>
        <PortfolioAccordion>
          <RentalIncomeAccordionSummary
            allTimeRentalIncome={totalDividendsPaid}
            rentalIncomePercent={blendedAnnualDividendPercent}
          />
          <RentalIncomeAccordionDetails
            allTimeDividends={totalDividendsPaid}
            prevDividend={latestDividend?.totalDividendsPaid}
            prevDividendDate={latestDividend?.endDate}
            rentalIncomePercent={blendedAnnualDividendPercent}
          />
        </PortfolioAccordion>
      </section>
      <section className={clsx('offering-list-section', 'body-section')}>
        <Box
          className="offering-list-header"
          top={offset + (topOffset ?? 0)}
          style={{ transition: `top ${transition}` }}
        >
          <PortfolioSectionHeaderTypography>
            <FormattedMessage
              id="number-properties-owned"
              defaultMessage="{number} Owned"
              values={{ number: accountOfferingBalances.length }}
            />
          </PortfolioSectionHeaderTypography>
          <Box display="flex" alignItems="center" gap={2}>
            {displayType === ROIMetricDisplayType.EQUITY && (
              <PortfolioEquityTooltip titleTypography={{ variant: 'h5' }} />
            )}
            <ROIMetricDisplayToggle displayType={displayType} setDisplayType={setDisplayType} />
          </Box>
        </Box>
        <List className="offering-list">
          {accountOfferingBalances.map((accountOfferingBalance) => {
            const path = generatePath(ROUTES.properties.propertyDetails, {
              idOrShortName: accountOfferingBalance.shortName,
            });

            return (
              <PortfolioOfferingListItem
                key={accountOfferingBalance.id}
                accountOfferingBalance={accountOfferingBalance}
                className="offering-list-item"
                displayType={displayType}
                onClick={() => navigate(path)}
              >
                <Link className="property-details-link" to={path}>
                  <FormattedMessage id="view-property-details-page" defaultMessage="View Property Details Page" />
                </Link>
              </PortfolioOfferingListItem>
            );
          })}
        </List>
      </section>
    </StyledPortfolio>
  );
};
